import { useMemo } from 'react'
import { format } from 'date-fns'
import { QueueStatusEnum } from '../../../../AppConstant'
import { useRecording } from '../../../../hooks/useRecording'
import {
  currentPatientAsync,
  resetElapsedSeconds,
  setSessionStatus,
  startChiropracticAsync,
  stopChiropracticAsync,
  resetCurrentPatient,
  undoVisitAsync,
} from '../../../../redux/flows/portal/currentPatient'
import {
  callInNextVisitAsync,
  resetCallInNextVisitStatus,
  resetPriorityVisit,
  setCurrentVisit,
  setPriorityVisit,
} from '../../../../redux/flows/portal/lobby'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'
import { COLORS } from '../../../../tokens'

const usePatientProfile = () => {
  const dispatch = useAppDispatch()

  const currentVisit = useAppSelector((state) => state.lobbyPortal.currentVisit)
  const currentPatient = useAppSelector((state) => state.currentPatientPortal.currentPatient)
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const sessionStatus = useAppSelector((state) => state.currentPatientPortal.sessionStatus)
  const notesUploadUrl = useAppSelector((state) => state.currentPatientPortal.notesUploadUrl)
  const elapsedSeconds = useAppSelector((state) => state.currentPatientPortal.elapsedSeconds)

  const startChiropracticStatus = useAppSelector((state) => state.currentPatientPortal.startChiropracticStatus)
  const stopChiropracticStatus = useAppSelector((state) => state.currentPatientPortal.stopChiropracticStatus)
  const callInNextVisitStatus = useAppSelector((state) => state.lobbyPortal.callInNextVisitStatus)
  const priorityVisit = useAppSelector((state) => state.lobbyPortal.priorityVisit)
  const undoVisitStatus = useAppSelector((state) => state.currentPatientPortal.undoVisitStatus)

  const { startRecording, stopRecording, uploadNotesUrl } = useRecording()

  /* ------------------------------------------------------------------
      ACTION HANDLERS
  ------------------------------------------------------------------ */
  const handleStartOrStopSession = () => {
    if (startChiropracticStatus === APIStatus.pending || stopChiropracticStatus === APIStatus.pending) {
      return
    }

    if (sessionStatus === QueueStatusEnum.WAITING) {
      dispatch(resetElapsedSeconds()) // Reset timer when starting session
      startRecording()
      dispatch(startChiropracticAsync(currentVisit?.visitId))
    } else if (sessionStatus === QueueStatusEnum.SERVICE_IN_PROGRESS) {
      uploadNotesUrl(notesUploadUrl)
      stopRecording()
      dispatch(stopChiropracticAsync({ visitId: currentVisit?.visitId }))
      // Session will be marked as COMPLETED by the stopChiropracticAsync reducer
    } else {
      // Session is COMPLETED - clear currentVisit before calling next visit
      // if (sessionStatus === QueueStatusEnum.COMPLETED) {
      //   dispatch(setSessionStatus(QueueStatusEnum.WAITING))
      //   dispatch(setCurrentVisit(null))
      // }
      dispatch(
        callInNextVisitAsync({
          queue: queue.checkedIn,
          priorityVisit: {
            id: priorityVisit?.id || '',
            name: priorityVisit?.name || '',
          },
        }),
      )
    }
  }

  const handleCallInNextVisit = () => {
    // if (sessionStatus === QueueStatusEnum.COMPLETED) {
    //   dispatch(setSessionStatus(QueueStatusEnum.WAITING))
    //   dispatch(setCurrentVisit(null))
    //   // Clear currentVisit when doctor clicks the button after completion
    // }
    dispatch(
      callInNextVisitAsync({
        queue: queue.checkedIn,
        priorityVisit: {
          id: priorityVisit?.id || '',
          name: priorityVisit?.name || '',
        },
      }),
    )
  }

  const handleUndoVisit = () => {
    if (undoVisitStatus === APIStatus.pending) {
      return
    }
    dispatch(undoVisitAsync({ visitId: currentVisit?.visitId || '' }))
  }

  const handleDone = () => {
    if (sessionStatus === QueueStatusEnum.COMPLETED) {
      dispatch(setSessionStatus(QueueStatusEnum.WAITING))
      dispatch(setCurrentVisit(null))
      dispatch(resetCurrentPatient())
    }
  }

  const handlePriorityClick = (item: any) => {
    if (callInNextVisitStatus === APIStatus.pending || sessionStatus === QueueStatusEnum.SERVICE_IN_PROGRESS) return

    // If clicking on the already-selected priority visit, deselect it
    if (priorityVisit?.id === item.visitId) {
      dispatch(resetPriorityVisit())
      return
    }

    // Only set priority for non-first items

    if (filteredAppointments.length > 1) {
      const priorityVisit = {
        id: item.visitId,
        name: item.visit?.patient?.user?.firstname || '',
      }
      dispatch(setPriorityVisit(priorityVisit))
    }
  }

  /* ------------------------------------------------------------------
      FORMATTED DOB
  ------------------------------------------------------------------ */
  const formattedDate = useMemo(() => {
    const dob = currentPatient?.patient?.user?.dob
    if (!dob) return { dayName: '-', monthDay: '-' }

    const today = new Date()
    const birth = new Date(dob)

    // Extract month/day using UTC to prevent TZ errors
    const month = birth.getUTCMonth()
    const day = birth.getUTCDate()

    const alignedDate = new Date(today.getUTCFullYear(), month, day)

    return {
      dayName: format(alignedDate, 'EEEE'),
      monthDay: format(alignedDate, 'MMM d'),
    }
  }, [currentPatient])

  const sessionDurationMinutes = currentPatient?.currentVisit?.needsFullSlot ? 15 : 5
  const sessionDurationSeconds = sessionDurationMinutes * 60

  /* ------------------------------------------------------------------
      COMPUTED UI VALUES
  ------------------------------------------------------------------ */

  const filteredAppointments = useMemo(() => {
    const checked = queue.checkedIn ?? []
    if (checked.length === 0) return []

    // 1) take earliest up to 3 (queue is earliest -> latest)
    const earliestThree = checked.slice(0, 3) // e.g. [jesica, andrew, tom]

    // 2) use earliestThree as display (no reversal)
    const display = earliestThree

    // 3) if no priority, return display
    if (!priorityVisit?.id) return display

    // 4) if priority is already in earliestThree (or display), return display
    const isPriorityInEarliest = earliestThree.some((v) => v.visitId === priorityVisit.id)
    if (isPriorityInEarliest) return display

    // 5) find full priority record from the full checked in list
    const priorityRecord = checked.find((v) => v.visitId === priorityVisit.id)
    if (!priorityRecord) {
      // priority not found in checked-in list — just return display unchanged
      return display
    }

    // 6) If we have exactly 3 display items, replace the bottom-most (index 2)
    if (display.length === 3) {
      const replaced = [...display]
      replaced[2] = priorityRecord
      return replaced
    }

    // 7) If display has < 3 items (1 or 2), put priority at the bottom so it's visible
    return [...display, priorityRecord]
  }, [queue.checkedIn, priorityVisit])

  const sessionTime = useMemo(() => {
    const minutes = Math.floor(elapsedSeconds / 60)
    const seconds = elapsedSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
  }, [elapsedSeconds])

  const sessionProgress = useMemo(() => {
    if (sessionDurationSeconds === 0) return 0
    return Math.min((elapsedSeconds / sessionDurationSeconds) * 100, 100)
  }, [elapsedSeconds, sessionDurationSeconds])

  const colorInfo = useMemo(() => {
    // Red when session time is fully elapsed
    if (sessionProgress >= 100) {
      return { backgroundColor: '#E94235', color: COLORS.textSecondary, border: 'none' }
    }

    // Yellow when more than 80%
    if (sessionProgress >= 80) {
      return { backgroundColor: '#FFCE00', color: COLORS.textPrimary, border: 'none' }
    }

    // Default states
    if (
      currentPatient?.currentVisit?.status === QueueStatusEnum.CALLED_IN &&
      sessionStatus !== QueueStatusEnum.SERVICE_IN_PROGRESS
    ) {
      return {
        backgroundColor: COLORS.primary,
        color: COLORS.textSecondary,
        border: COLORS.borderPrimary,
      }
    }

    return {
      backgroundColor: COLORS.textSecondary,
      color: COLORS.textPrimary,
      border: COLORS.borderPrimary,
    }
  }, [
    sessionProgress,
    currentPatient?.currentVisit?.status,
    callInNextVisitStatus,
    startChiropracticStatus,
    stopChiropracticStatus,
    sessionStatus,
    elapsedSeconds,
  ])

  /* ------------------------------------------------------------------
      RETURN VALUES
  ------------------------------------------------------------------ */

  return {
    currentPatient,
    currentVisit,
    queue,
    sessionStatus,
    priorityVisit,
    stopChiropracticStatus,
    // Actions
    handleStartOrStopSession,
    handleCallInNextVisit,
    handlePriorityClick,
    handleDone,
    // UI helpers
    formattedDate,
    sessionTime,
    sessionProgress,
    colorInfo,
    handleUndoVisit,
    filteredAppointments,
  }
}

export default usePatientProfile
